import { useMutation } from '@apollo/client'
import clsx from 'clsx'
import { FormikErrors, useFormik } from 'formik'
import { useState, useEffect, useContext } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { UPDATE_BG_ACCOUNT, UPDATE_USER } from '../graphql/mutation'
import { GET_BG_ACCOUNT_BY_ID } from '../graphql/query'
import { toAbsoluteUrl } from '../helpers'
import * as Yup from 'yup'
import UserContext from '../context/UserContext'

interface IProps {
  closeBusinessModal?: () => void
  loading: any
  error: any
  data: any
}

const OnboardingSchema = Yup.object().shape({
  organization: Yup.string()
    .min(2, 'Minimum 2 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('organization is required'),
  location: Yup.string()
    .min(2, 'Minimum 2 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('location is required')
})

export const EditBusinessModal: React.FC<IProps> = ({
  closeBusinessModal,
  loading,
  error,
  data
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const { currentUser } = useContext(UserContext)
  const [updateBgAccount] = useMutation(UPDATE_BG_ACCOUNT, {
    refetchQueries: [{ query: GET_BG_ACCOUNT_BY_ID }]
  })
  const [updateUser] = useMutation(UPDATE_USER)
  const [selectedOptions, setSelectedOptions] = useState({
    industryType: '',
    companySize: ''
  })
  const navigate = useNavigate()

  const handleDropdownSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const name = e.target.name
    const value = e.target.value

    setSelectedOptions((prevValue) => ({
      ...prevValue,
      [name]: value
    }))
  }

  useEffect(() => {
    if (!!data && !!data.getBgAccountById.industry) {
      setSelectedOptions((prevValue) => ({
        ...prevValue,
        industryType: data.getBgAccountById.industry
      }))
    }
    if (!!data && !!data.getBgAccountById.companySize) {
      setSelectedOptions((prevValue) => ({
        ...prevValue,
        companySize: data.getBgAccountById.companySize
      }))
    }
  }, [data])

  const initialValues = {
    organization:
      !!data && !loading && !error && data.getBgAccountById.organization
        ? data.getBgAccountById.organization
        : '',
    location:
      !!data && !loading && !error && data.getBgAccountById.location
        ? data.getBgAccountById.location
        : ''
  }

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: OnboardingSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      console.log(values)
      setIsLoading(true)
      updateBgAccount({
        variables: {
          input: {
            organization: values.organization,
            location: values.location,
            industry: selectedOptions.industryType,
            companySize: selectedOptions.companySize
          }
        }
      })
        .then((res: any) => {
          setIsLoading(false)
          resetForm()
          !!closeBusinessModal && closeBusinessModal()
          if (res.data.updateBgAccount.status >= 200 && res.data.updateBgAccount.status < 300) {
            // toast.success(res.data.updateBgAccount.message)
            !!closeBusinessModal && closeBusinessModal()
            // Mark onboardingDone as true for the admin user
            !closeBusinessModal &&
              updateUser({
                variables: {
                  input: {
                    id: parseInt(currentUser.id),
                    name: currentUser.name,
                    onboardingDone: true
                  }
                }
              }).then((res: any) => {
                if (res.data.updateUser.status >= 200 && res.data.updateUser.status < 300) {
                  !closeBusinessModal && navigate('/dashboard')
                }
              })
          } else {
            toast.error(res.data.updateBgAccount.message)
            setSubmitting(false)
            !!closeBusinessModal && closeBusinessModal()
          }
        })
        .catch((err) => {
          setIsLoading(false)
          setSubmitting(false)
          !!closeBusinessModal && closeBusinessModal()
          toast.error('Sorry! Unexpected Error.')
          console.error(err)
        })
    }
  })

  if (loading) {
    return <h1>Loading...</h1>
  }

  if (error) {
    return <p>error</p>
  }

  const getErrorMessage = (error: string | string[] | FormikErrors<any> | FormikErrors<any>[]) => {
    if (typeof error === 'string') {
      return error
    } else if (Array.isArray(error)) {
      return error.join(', ')
    } else {
      return JSON.stringify(error)
    }
  }

  return (
    <div
      className='d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed'
      style={{
        backgroundImage: `url(${toAbsoluteUrl('/media/illustrations/sketchy-1/14.png')})`
      }}
    >
      <div className='d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20'>
        {/* begin::Logo */}
        {/* <a href='#' className='mb-12'>
            <img alt='Logo' src={toAbsoluteUrl('/media/logos/logo.png')} className='h-65px' />
          </a> */}
        {/* end::Logo */}
        {/* begin::Wrapper */}
        <div className='w-lg-500px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto'>
          <form
            className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
            noValidate
            id='kt_login_signup_form'
            onSubmit={formik.handleSubmit}
          >
            {/* begin::Heading */}
            {!closeBusinessModal && (
              <div className='mb-10 text-center'>
                {/* begin::Title */}
                <h2 className='text-dark mb-4'> You are almost Done !</h2>
                <p className='text-muted fs-5'>Tell us little bit about your business. </p>
              </div>
            )}

            {formik.status && (
              <div className='mb-lg-15 alert alert-danger'>
                <div className='alert-text font-weight-bold'>{formik.status}</div>
              </div>
            )}

            <div className='fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6 required'>Organization:</label>
              <input
                placeholder='Organization'
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('organization')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.organization && formik.errors.organization
                  },
                  {
                    'is-valid': formik.touched.organization && !formik.errors.organization
                  }
                )}
              />
              {formik.touched.organization && formik.errors.organization && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{getErrorMessage(formik.errors.organization)}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='mb-10 fv-row'>
              <label className='form-label fw-bolder text-dark fs-6 required'>
                What is the employee size of your company?
              </label>
              <div className='position-relative mb-3'>
                <select
                  className=' form-select form-select-solid form-select '
                  value={selectedOptions.companySize}
                  onChange={handleDropdownSelect}
                  name='companySize'
                >
                  <option key='blankKey' hidden>
                    Select
                  </option>
                  <option value='belowTwenty'>Below 20</option>
                  <option value='twentyOneToFifty' selected>
                    21 to 50
                  </option>
                  <option value='fiftyOneToTwoHundred'>51 to 100</option>
                  <option value='twoHundredOneToFiveHundred'>201 to 500</option>
                  <option value='fiveHundredOneToThousand'>501 to 1000</option>
                  <option value='thousandPlus'>1000+</option>
                </select>
              </div>
            </div>

            <div className='mb-10 fv-row'>
              <label className='form-label fw-bolder text-dark fs-6 required'>
                What is your industry type?
              </label>
              <div className='position-relative mb-3'>
                <select
                  className=' form-select form-select-solid form-select '
                  value={selectedOptions.industryType}
                  onChange={handleDropdownSelect}
                  name='industryType'
                >
                  <option key='blankKey' hidden>
                    Select
                  </option>
                  <option value='Food & Beverages'>Food & Beverages</option>
                  <option value='Arts & Entertainment'>Arts & Entertainment</option>
                  <option value='Agency & Consulting'>Agency & Consulting</option>
                  <option value='Retail & E-commerce'>Retail & E-commerce</option>
                  <option value='Hospitality & Tourism'>Hospitality & Tourism</option>
                  <option value='Automative'> Automative</option>
                  <option value='Government & Nonprofit'>Government & Nonprofit</option>
                  <option value='Financial Services'>Financial Services</option>
                  <option value='Real Estate'>Real Estate</option>
                  <option value='Software & Technology'>Software & Technology</option>
                  <option value='Healthcare & Medical Services'>
                    Healthcare & Medical Services
                  </option>
                  <option value='Legal Services'>Legal Services</option>
                  <option value='Education'>Education</option>
                  <option value='Other'>Other</option>
                </select>
              </div>
            </div>

            <div className='fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6 required'>Location</label>
              <input
                placeholder='Location'
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('location')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.location && formik.errors.location
                  },
                  {
                    'is-valid': formik.touched.location && !formik.errors.location
                  }
                )}
              />
              {formik.touched.location && formik.errors.location && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{getErrorMessage(formik.errors.location)}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='text-center'>
              <button
                type='submit'
                id='kt_sign_up_submit'
                className='btn btn-lg btn-primary w-100 mb-5'
                disabled={formik.isSubmitting || !formik.isValid}
              >
                {!isLoading && <span className='indicator-label'>Submit</span>}
                {isLoading && (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    Please wait...{' '}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>

              {!closeBusinessModal && (
                <Link to='/bots/all-bots'>
                  <button
                    type='button'
                    id='kt_login_signup_form_cancel_button'
                    className='btn btn-lg btn-secondary w-100 mb-5'
                  >
                    Do it Later
                  </button>
                </Link>
              )}

              {!!closeBusinessModal && (
                <button
                  type='button'
                  id='kt_login_signup_form_cancel_button'
                  className='btn btn-lg btn-secondary w-100 mb-5'
                  onClick={closeBusinessModal}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
        {/* end::Wrapper */}
      </div>
    </div>
  )
}

export default EditBusinessModal
